import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, CheckCircle2, AlertCircle, Building2, Settings2, ShieldAlert } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/api";

const SystemSettings = () => {
  // --- STATE QUẢN LÝ QUYỀN TRUY CẬP ---
  const [currentUser, setCurrentUser] = useState({ role: 'user', facility_id: null });

  // --- STATE DỮ LIỆU ---
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState(''); // Lưu ID khóa chính (Primary Key)
  const [rules, setRules] = useState([]);
  
  // --- STATE TRẠNG THÁI GIAO DIỆN ---
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  const showToast = (message, isError = false) => {
    setToastMessage({ text: message, isError });
    setTimeout(() => setToastMessage(null), 3500);
  };

  // 1. CHUẨN HÓA: Gộp bước lấy User Info và Load Danh sách Cơ sở vào 1 lần Mount duy nhất
  useEffect(() => {
    const initializeData = async () => {
      // Đọc thông tin User
      let role = 'user';
      let facId = null;
      try {
        const userInfoStr = localStorage.getItem('user_info');
        if (userInfoStr) {
          const userInfo = JSON.parse(userInfoStr);
          role = userInfo.role ? userInfo.role.toLowerCase() : 'user';
          facId = userInfo.facility_id;
          setCurrentUser({ role, facility_id: facId });
        }
      } catch (e) {
        console.error("Lỗi khi đọc thông tin user:", e);
      }

      // Load Facilities (kèm Token)
      try {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${API_BASE}/facilities`, {
          headers: { "Authorization": `Bearer ${token}` }
        }); 
        
        if (response.ok) {
          const data = await response.json();
          const facList = data.data || data; 
          setFacilities(facList);
          
          if (facList.length > 0) {
            if (role === 'user' && facId) {
              setFacilityId(facId); // Gán đúng ID khóa chính của User
            } else {
              // Admin: Mặc định chọn cơ sở 94019, nhớ lấy ID khóa chính chứ không lấy Mã KCB
              const defaultFac = facList.find(f => String(f.ma_cskcb) === '94019');
              setFacilityId(defaultFac ? defaultFac.id : facList[0].id);
            }
          }
        }
      } catch (error) {
        console.error("Lỗi khi tải danh sách CS KCB:", error);
        showToast("Không thể tải danh sách cơ sở y tế", true);
      }
    };

    initializeData();
  }, []);

  // 2. Fetch danh sách Rules theo ID Cơ sở (Đã thêm Token)
  useEffect(() => {
    const fetchFacilityRules = async (id) => {
      if (!id) return;
      setIsLoading(true);
      try {
        const token = localStorage.getItem('admin_token');
        // Lúc này id truyền đi chắc chắn là Primary Key (số nguyên)
        const response = await fetch(`${API_BASE}/settings/rules/${id}`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Lỗi server');
        }
        const data = await response.json();
        setRules(data);
      } catch (error) {
        console.error("Lỗi fetch rules:", error.message);
        setRules([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFacilityRules(facilityId);
  }, [facilityId]);

  const handleToggle = (key) => {
    setRules(prev => prev.map(r => 
      r.rule_key === key ? { ...r, is_active: !r.is_active } : r
    ));
  };

  // 3. Hàm lưu cấu hình (Đã gắn Token và chuẩn hóa Payload)
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        facility_id: parseInt(facilityId), // Đảm bảo luôn gửi số nguyên cho Backend
        rules: rules.map(({ rule_key, is_active, description }) => ({
          rule_key,
          is_active,
          description: description || "" 
        }))
      };

      const response = await fetch(`${API_BASE}/settings/rules`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}` 
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || "Lỗi khi lưu");
      }

      showToast("Cấu hình đã được ghi nhận vào hệ thống!");
    } catch (e) {
      console.error("Lỗi lưu:", e.message);
      showToast(e.message || "Không thể lưu cấu hình. Vui lòng thử lại!", true);
    } finally {
      setIsSaving(false);
    }
  };

  // Lấy tên cơ sở để hiển thị
  const getFacilityName = () => {
    const fac = facilities.find(f => String(f.id) === String(facilityId));
    return fac ? `${fac.ma_cskcb} - ${fac.ten_cskcb}` : `Cơ sở ID: ${facilityId}`;
  };

  return (
    <div className="flex flex-col h-full w-full gap-4 relative">
      
      {/* ================= TOAST THÔNG BÁO ================= */}
      {toastMessage && (
        <div className={`fixed bottom-24 lg:bottom-10 right-1/2 translate-x-1/2 lg:translate-x-0 lg:right-8 z-[200] text-white px-5 py-3.5 rounded-2xl shadow-2xl flex items-center gap-3 animate-slide-up-fade whitespace-nowrap ${toastMessage.isError ? 'bg-rose-600' : 'bg-slate-800'}`}>
          {toastMessage.isError ? <AlertCircle size={18} className="text-rose-200" /> : <CheckCircle2 size={18} className="text-emerald-400" />}
          <span className="text-sm font-bold">{toastMessage.text}</span>
        </div>
      )}

      {/* ================= TOOLBAR ================= */}
      <div className="shrink-0 bg-white/70 backdrop-blur-xl border border-white rounded-2xl px-5 py-4 flex flex-col md:flex-row justify-between items-start md:items-center shadow-sm gap-4 z-10">
        
        <div className="flex items-center gap-3">
          <div className="p-2 bg-sky-50 text-sky-600 rounded-xl shadow-sm border border-sky-100 shrink-0">
            <Settings2 size={22} strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-base lg:text-lg font-black tracking-tight text-slate-800 leading-tight">Cấu hình Hệ thống</h1>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Quản lý luật giám định XML</p>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          {/* VÙNG CHỌN CƠ SỞ */}
          <div className="flex items-center gap-2 bg-white/80 border border-slate-200/60 rounded-xl p-1 shadow-sm flex-1 md:flex-none transition-all focus-within:border-sky-300 focus-within:ring-2 focus-within:ring-sky-50">
            <div className="pl-3 pr-1 text-slate-400"><Building2 size={16}/></div>
            
            {currentUser.role === 'admin' ? (
              <select 
                value={facilityId} 
                onChange={(e) => setFacilityId(e.target.value)}
                disabled={facilities.length === 0}
                className="bg-transparent text-sm font-bold text-slate-700 outline-none cursor-pointer py-1.5 pr-4 w-full md:w-64 disabled:opacity-50"
              >
                {facilities.length === 0 ? (
                  <option value="">Đang tải danh sách...</option>
                ) : (
                  facilities.map(fac => (
                    // QUAN TRỌNG: value phải luôn là ID (Primary Key)
                    <option key={fac.id} value={fac.id}>
                      {fac.ma_cskcb} - {fac.ten_cskcb || fac.name}
                    </option>
                  ))
                )}
              </select>
            ) : (
              <div className="py-1.5 pr-4 pl-1 text-sm font-bold text-slate-700 w-full md:w-64 line-clamp-1">
                {getFacilityName()}
              </div>
            )}
          </div>

          <button 
            onClick={handleSave}
            disabled={isSaving || isLoading || rules.length === 0}
            className="flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-700 disabled:bg-slate-300 text-white px-5 py-2.5 rounded-xl font-bold transition-all shadow-sm active:scale-95 shrink-0"
          >
            {isSaving ? <RefreshCw className="animate-spin" size={16} /> : <Save size={16} />}
            <span className="hidden sm:inline">Lưu cấu hình</span>
          </button>
        </div>
      </div>

      {/* ================= NỘI DUNG CHÍNH (GRID LUẬT) ================= */}
      <div className="flex-1 overflow-y-auto custom-scrollbar pb-4">
        {isLoading ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-400">
            <RefreshCw size={40} className="animate-spin text-sky-400 mb-4 opacity-70" />
            <p className="font-bold text-slate-500 text-sm">Đang đồng bộ dữ liệu cấu hình...</p>
          </div>
        ) : rules.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-400 bg-white/40 backdrop-blur-sm rounded-2xl border border-white border-dashed">
            <ShieldAlert size={48} className="text-slate-300 mb-4" />
            <p className="font-bold text-slate-500 text-lg">Chưa có luật giám định nào được thiết lập</p>
            <p className="text-xs font-medium text-slate-400 mt-1">Vui lòng kiểm tra lại Cơ sở y tế hoặc Database.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rules.map((rule) => (
              <div 
                key={rule.rule_key} 
                className={`p-5 rounded-2xl border transition-all duration-300 relative overflow-hidden group ${
                  rule.is_active 
                    ? 'bg-white/90 backdrop-blur-md border-sky-200 shadow-sm hover:shadow-md' 
                    : 'bg-white/50 border-slate-200/60 opacity-80 hover:opacity-100'
                }`}
              >
                {/* Thanh Accent dọc theo Card */}
                <div className={`absolute left-0 top-0 bottom-0 w-1.5 transition-colors duration-300 ${
                  rule.is_active ? 'bg-sky-500' : 'bg-slate-200'
                }`}></div>
                
                <div className="flex justify-between items-start ml-2">
                  <div className="pr-4 flex-1">
                    <span className={`text-[10px] font-black px-2.5 py-1 rounded-lg border uppercase tracking-wider ${
                      rule.is_active 
                        ? 'bg-sky-50 text-sky-600 border-sky-100' 
                        : 'bg-slate-100 text-slate-500 border-slate-200'
                    }`}>
                      {rule.rule_key}
                    </span>
                    <p className={`text-sm font-bold mt-3 leading-relaxed ${
                      rule.is_active ? 'text-slate-800' : 'text-slate-500'
                    }`}>
                      {rule.description || "Chưa có mô tả cho quy tắc này."}
                    </p>
                  </div>
                  
                  {/* Nút Toggle Switch */}
                  <button 
                    onClick={() => handleToggle(rule.rule_key)}
                    className={`shrink-0 w-12 h-6 rounded-full relative transition-colors duration-300 border-2 ${
                      rule.is_active ? 'bg-sky-500 border-sky-500' : 'bg-slate-200 border-slate-200 hover:bg-slate-300 hover:border-slate-300'
                    }`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all duration-300 shadow-sm ${
                      rule.is_active ? 'left-[22px]' : 'left-0.5'
                    }`} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================= KEYFRAMES CSS ================= */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 8px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        
        @keyframes slideUpFade {
          from { opacity: 0; transform: translateY(20px) translateX(-50%); }
          to { opacity: 1; transform: translateY(0) translateX(-50%); }
        }
        @media (min-width: 1024px) {
          @keyframes slideUpFade {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        }
        .animate-slide-up-fade {
          animation: slideUpFade 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
    </div>
  );
};

export default SystemSettings;